/**
 * Business Logic Layer - pure functions, no React. Mirrors
 * _features/usageCompilation/modals/UsageCompilationFormModal/utils/
 * usageCompilationFormRules.js's convertFromSelectableObject/
 * flattenSelectableFields shape, applied to the `role` field only.
 */

/**
 * Unwraps the role field's stored value (a full role object, matching its
 * `_id`/`name` key shape - see utils/userManagementRowMapping.js) down to
 * the plain _id the backend expects. Since the role field is now given
 * isObject/valueSelector: "_id" (see hooks/useUserManagementFormSchema.js),
 * this is a straight id unwrap - no name->id lookup map needed, unlike the
 * old modal.
 */
export function flattenRoleField(validData) {
  const role = validData?.role;
  if (!role) return validData;

  return {
    ...validData,
    role: typeof role === "object" && role._id ? role._id : role,
  };
}

/**
 * Unwraps the combined "<countryCode> <phoneNumber>" string PhoneInput
 * produces (see _components/ui/PhoneInput's own header comment) back into
 * the two separate fields the backend/table actually store
 * (countryCode/phoneNumber - see userManagement/utils/columnDefinitions.jsx's
 * edit-prefill, which does the opposite join to seed this same field).
 * Without this step the combined string got saved verbatim into the
 * `phoneNumber` column on every edit, so re-editing a row (even untouched)
 * kept re-baking the country code into the number - showing as a duplicated
 * value in the table (e.g. "950232129 950232129").
 */
export function splitPhoneField(validData) {
  const raw = validData?.phoneNumber;
  if (typeof raw !== "string" || !raw.trim()) return validData;

  const [countryCode, ...rest] = raw.trim().split(" ");
  const phoneNumber = rest.join(" ").trim();
  if (!phoneNumber) return validData;

  return { ...validData, countryCode, phoneNumber };
}

/**
 * Builds the edit-mode organisationUpdate sub-object the backend expects
 * when any org-scoped field changed - ported from the old modal's submit
 * handler (designation/jobRole/department fall back to the row's current
 * value when the field wasn't touched).
 */
export function buildOrganisationUpdate(validData, rowData, currentOrgId) {
  if (!validData.designation && !validData.jobRole && !validData.department) {
    return null;
  }

  const currentOrg = rowData?.organisations?.find((org) => org?.orgId === currentOrgId);
  if (!currentOrg) return null;

  return {
    orgId: currentOrg.orgId,
    designation: validData.designation || currentOrg.designation,
    jobRole: validData.jobRole || currentOrg.jobRole,
    department: validData.department || currentOrg.department,
  };
}

export function formatUserSuccessMessage(validData, mode) {
  return `${validData?.firstName ?? ""} ${validData?.lastName ?? ""} - User successfully ${mode === "create" ? "created" : "updated"}`;
}
